import { Injectable, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type VhTheme = 'light' | 'dark';

const STORAGE_KEY = 'vh-theme';

/**
 * Controls the VendorHub light/dark theme.
 *
 * - Persists the user choice in `localStorage` (browser only).
 * - Falls back to `prefers-color-scheme` on first visit.
 * - Sets `data-theme` on `<html>` so all CSS variables swap instantly.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly _theme = signal<VhTheme>(this.readInitial());

  readonly theme = this._theme.asReadonly();
  readonly isDark = computed(() => this._theme() === 'dark');

  constructor() {
    effect(() => {
      if (!this.isBrowser) return;
      const t = this._theme();
      document.documentElement.setAttribute('data-theme', t);
      try {
        localStorage.setItem(STORAGE_KEY, t);
      } catch {
        /* ignore private mode / storage full */
      }
    });
  }

  toggle(): void {
    this._theme.update((t) => (t === 'light' ? 'dark' : 'light'));
  }

  set(t: VhTheme): void {
    this._theme.set(t);
  }

  private readInitial(): VhTheme {
    if (!this.isBrowser) return 'light';
    try {
      const stored = localStorage.getItem(STORAGE_KEY) as VhTheme | null;
      if (stored === 'light' || stored === 'dark') return stored;
    } catch {
      /* ignore */
    }
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
}
